import Link from 'next/link'

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  href,
  type = 'button',
  disabled = false,
  loading = false,
  fullWidth = false,
  className = '',
  ...props
}) {
  const baseStyles =
    'inline-flex items-center justify-center font-sans font-semibold uppercase tracking-widest transition-all focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foreground'

  const variants = {
    primary:
      'border-2 border-foreground bg-foreground text-background hover:bg-transparent hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed',
    secondary:
      'border-2 border-foreground bg-transparent text-foreground hover:bg-foreground hover:text-background disabled:opacity-50 disabled:cursor-not-allowed',
    outline:
      'border-2 border-foreground/30 bg-transparent text-foreground hover:border-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed',
    ghost:
      'bg-transparent text-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed',
    destructive:
      'border-2 border-destructive bg-destructive text-destructive-foreground hover:bg-transparent hover:text-destructive disabled:opacity-50 disabled:cursor-not-allowed',
    muted:
      'border-2 border-border bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed',
    left: 'border-2 border-blue-600 bg-blue-600 text-white hover:bg-transparent hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed dark:border-blue-500 dark:bg-blue-500 dark:hover:text-blue-500',
    right:
      'border-2 border-red-600 bg-red-600 text-white hover:bg-transparent hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed dark:border-red-500 dark:bg-red-500 dark:hover:text-red-500',
  }

  const sizes = {
    xs: 'px-3 py-1.5 text-[10px]',
    sm: 'px-4 py-2 text-[11px]',
    md: 'px-6 py-3 text-xs',
    lg: 'px-8 py-3.5 text-xs',
    xl: 'px-10 py-4 text-sm',
  }

  const widthStyles = fullWidth ? 'w-full' : ''
  const disabledStyles = disabled || loading ? 'cursor-not-allowed' : ''

  const combinedClassName = `${baseStyles} ${variants[variant]} ${sizes[size]} ${widthStyles} ${disabledStyles} ${className}`

  // Loading spinner component
  const LoadingSpinner = () => (
    <svg
      className="animate-spin -ml-1 mr-2 h-4 w-4"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  )

  // If href is provided, render as Link
  if (href) {
    return (
      <Link href={href} className={combinedClassName} {...props}>
        {loading && <LoadingSpinner />}
        {children}
      </Link>
    )
  }

  // Otherwise render as button
  return (
    <button
      type={type}
      disabled={disabled || loading}
      className={combinedClassName}
      {...props}
    >
      {loading && <LoadingSpinner />}
      {children}
    </button>
  )
}

// Export variants for easy reference
Button.variants = {
  primary: 'primary',
  secondary: 'secondary',
  outline: 'outline',
  ghost: 'ghost',
  destructive: 'destructive',
  muted: 'muted',
  left: 'left',
  right: 'right',
}

Button.sizes = {
  xs: 'xs',
  sm: 'sm',
  md: 'md',
  lg: 'lg',
  xl: 'xl',
}
